import { memo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import numbro from 'numbro';

import { StationItemNo } from '../../../../constants/Games/gameStation';
import { getStationVisual } from '../../../../constants/Games/gameStationUtils';
import { SVGIconType } from '../../../../constants/svgIcon';
import useLocale from '../../../../hooks/useLocale';
import { TranslationLocale } from '../../../../i18n';
import { Space } from '../../../../models/SpaceStore';
import { Album, AlbumAudience } from '../../../../models/SpaceStore/Album';
import { Profile } from '../../../../models/UserStore';
import { timeAgo } from '../../../../utils/date';
import { getUiLocale } from '../../../../utils/i18n';
import { AvatarNode } from '../../AvatarNode';
import { SeparatorComponent } from '../../SeparatorComponent';
import { Skeleton } from '../../Skeleton';
import { SvgIcon } from '../../SVGIcon';

import styles from './StartStationCell.module.css';

export type StartStationCellProps = {
  album: Album;
  albumClassroom: Space;
  classroomOwnerProfile?: Profile | undefined | null;
  stationId: StationItemNo;
  name: string | undefined;
  startVideoThumbnailUrl: string | undefined;
  isUnlocked: boolean;
  onClickPlay?: () => void;
  onClickVideo?: () => void;
};

export const StartStationCell = memo((props: StartStationCellProps) => {
  const {
    album,
    albumClassroom,
    classroomOwnerProfile,
    stationId,
    // name,
    startVideoThumbnailUrl,
    isUnlocked,
    onClickPlay,
    onClickVideo,
  } = props;
  const { t, i18n } = useTranslation();
  const l = useLocale();

  const albumInfo = l(album.i18nGameSetInfo);

  const [isAlbumClassroomIconAvailable, setIsAlbumClassroomIconAvailable] =
    useState(true);

  const classroomInfo = l(albumClassroom.i18nClassroomInfo);

  // const classroomName =
  //   albumClassroom.type === SpaceType.Home
  //     ? classroomOwnerProfile?.name
  //     : classroomInfo?.name;

  const classroomName = classroomInfo?.name;

  return (
    <div className={styles.startStationCell} onClick={onClickPlay}>
      {/* Station Image */}
      <div className={styles.component}>
        <SvgIcon
          name={SVGIconType.ProgressArrow}
          width={32}
          height={32}
          color={isUnlocked ? '#ffffff' : '#b2b2b2'}
          style={{
            filter: 'drop-shadow(0px 0px 1px #00000050)', // Drop shadow
            marginBottom: -6,
            opacity: 0.85,
          }}
        />
        <img
          src={getStationVisual(stationId)?.filePrefix}
          className={styles.largeIcon}
          alt="Large Icon"
        />
        <div className={styles.textOverlay}>{t('Start_NOUN')}</div>
      </div>
      {/* CellContent */}
      <div
        className={`${styles.gameComponent} ${
          isUnlocked ? styles.gameComponentUnlocked : styles.gameComponentLocked
        }`}
      >
        <div className={styles.leftPart}>
          <p className={styles.title}>{albumInfo?.name}</p>
          {startVideoThumbnailUrl && (
            <div
              className={styles.video}
              onClick={(event) => {
                event.stopPropagation();
                onClickVideo?.();
              }}
            >
              <img
                className={styles.videoThumbnail}
                src={startVideoThumbnailUrl}
              />
              {/* <div className={styles.playIconWrapper}>
                <div className={styles.defaultPlayIcon}>
                  <svg width="64" height="64" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="32" fill="rgba(0, 0, 0, 0.6)" />
                    <path d="M26 20l16 12-16 12z" fill="white" />
                  </svg>
                </div>
              </div> */}
            </div>
          )}
          <div className={styles.lowerRow}>
            <div className={styles.info}>
              <SeparatorComponent
                separator={
                  <p className={`${styles.separateText} ${styles.greyText}`}>
                    ·
                  </p>
                }
              >
                <div className={styles.textComponent}>
                  {albumClassroom?.icon?.publicUrls?.small &&
                  isAlbumClassroomIconAvailable ? (
                    <img
                      src={albumClassroom?.icon?.publicUrls?.small}
                      onError={() => setIsAlbumClassroomIconAvailable(false)}
                      className={styles.spaceIcon}
                    />
                  ) : classroomOwnerProfile ? (
                    <AvatarNode profile={classroomOwnerProfile} size={28} />
                  ) : (
                    <Skeleton width="32px" height="32px" borderRadius="1rem" />
                  )}

                  <p className={`${styles.smallGreyText} ${styles.greyText}`}>
                    {classroomName}
                  </p>
                </div>
                {album ? (
                  <div className={`${styles.smallGreyText} ${styles.greyText}`}>
                    {timeAgo(
                      album.createdAt,
                      getUiLocale(i18n.language as TranslationLocale),
                    )}
                  </div>
                ) : null}

                {album ? (
                  <SvgIcon
                    name={
                      album.audience == AlbumAudience.Public
                        ? SVGIconType.Region
                        : album.audience == AlbumAudience.Members
                        ? SVGIconType.Member
                        : album.audience == AlbumAudience.Followers
                        ? SVGIconType.Friend
                        : SVGIconType.MembershipTier
                    }
                    className={`${styles.privacyIcon} ${styles.greyText}`}
                  />
                ) : null}

                {album &&
                album.statistics.gamePlayCount &&
                album.statistics.gamePlayCount > 0 ? (
                  <div className={`${styles.smallGreyText} ${styles.greyText}`}>
                    <Trans
                      i18nKey="{count} Plays"
                      values={{
                        count: numbro(album.playCount).format({
                          thousandSeparated: true,
                        }),
                      }}
                    />
                  </div>
                ) : null}
              </SeparatorComponent>
            </div>
          </div>
        </div>

        {/* {isUnlocked ? (
          <div className={styles.playButton}>
            <SvgIcon
              name={SVGIconType.Arrow}
              width={24}
              height={24}
              color="#46CBC0"
            />
          </div>
        ) : null} */}
      </div>
    </div>
  );
});
